export type Confidence = "high" | "medium" | "low";

export type SupportedVendor = "data-experts" | "fiona" | "elan-rlp" | "elan-nrw" | "elan-sh" | "ibalis" | "dianaweb" | "andi" | "hessen-polygone";

export type UnsupportedVendor = "polish-ewniosekplus" | "niedersachsen-alt" | "tatuk" | "hvu-polygone" | "fiona-no-crops" | "unknown";

export type Vendor = SupportedVendor | UnsupportedVendor;

export interface FormatClassification {
  vendor: Vendor;
  formatFamily: string;
  confidence: Confidence;
  detectionSignals: string[];
  supported: boolean;
  regionHint: string;
  // Application year (Antragsjahr) inferred from the ZIP — usually picked up
  // from filenames or folder names because every portal embeds it there
  // (e.g. `NW25AGR_…`, `Export_2025_DE03_*_Shape/`, `Antragsschläge 2025_POLYGONE/`).
  year?: number;
  // Optional human-readable note shown in the UI when `supported` is false.
  // Used to explain why a known-but-unsupported export cannot be parsed and
  // what the user should do instead.
  unsupportedReason?: string;
}

export interface CropRecord {
  fieldId: string;
  fieldName: string;
  cropCode: string;
  cropDescription: string;
  areaHa: number;
  fieldNumber?: string;
}

export interface ParserResult {
  method: string;
  crops: CropRecord[];
  warnings: string[];
  // Antragsjahr (application year) extracted from the file's content (XML
  // element or DBF column). When present, this overrides the filename-derived
  // year that classifyZip computes — it's strictly more authoritative.
  year?: number;
}

export interface ExtractionSummary {
  totalFields: number;
  totalArea: number;
  avgFieldSize: number;
  topCrop: { code: string; description: string; count: number } | null;
  cropDistribution: Record<string, number>;
}

export interface ExtractionResult {
  success: boolean;
  classification: FormatClassification;
  inventory: string[];
  extraction: {
    method: string;
    crops: CropRecord[];
    cropCount: number;
    totalArea: number;
    processingTime: number;
  } | null;
  summary: ExtractionSummary | null;
  errors: string[];
  warnings: string[];
}

export const ERROR_TYPES = {
  INVALID_ZIP: "File is not a valid ZIP archive",
  EMPTY_ZIP: "ZIP file contains no files",
  TOO_LARGE: "File exceeds the 50 MB size limit",
  UNSUPPORTED_FORMAT: "File format not yet supported",
  NO_CROPS_FOUND: "No crop data found in file",
  PARSING_ERROR: "Error parsing file structure",
  MISSING_FILES: "Required files missing from upload",
  TIMEOUT: "Extraction timed out after 30 seconds",
} as const;
